import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  Alert,
  IconButton,
  InputAdornment,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import useSignup from "../Api/signupApi";

export default function Signup() {
  const [form, setForm] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    countryCode: "91", // ✅ default India
    phoneNumber: "",
    dateOfBirth: "2010-01-01", // ✅ Default date set to January 1, 2010
  });

  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const { signup, error, loading } = useSignup();
  const navigate = useNavigate();

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Validation
  const validate = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!form.firstName) errors.firstName = "First name is required";
    if (!form.lastName) errors.lastName = "Last name is required";
    if (!form.email) errors.email = "Email is required";
    else if (!emailRegex.test(form.email)) errors.email = "Invalid email";

    if (!form.password) errors.password = "Password is required";
    else if (form.password.length < 6)
      errors.password = "Minimum 6 characters required";

    if (!form.confirmPassword)
      errors.confirmPassword = "Please confirm password";
    else if (form.password !== form.confirmPassword)
      errors.confirmPassword = "Passwords do not match";

    if (!form.phoneNumber) {
      errors.phoneNumber = "Phone number is required";
    } else if (form.phoneNumber.length !== 10) {
      errors.phoneNumber = "Must be a 10-digit number";
    }

    if (!form.dateOfBirth) {
      errors.dateOfBirth = "Date of birth is required";
    } else {
      const selectedDate = new Date(form.dateOfBirth);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate > today) {
        errors.dateOfBirth = "Date of birth cannot be in the future";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      email: form.email,
      password: form.password,
      firstName: form.firstName,
      lastName: form.lastName,
      countryCode: form.countryCode, // ✅ separate field
      phoneNumber: form.phoneNumber, // ✅ only digits
      dateOfBirth: `${form.dateOfBirth}T00:00:00Z`,
    };

    try {
      await signup(payload);
      alert("Signup successful!");
      navigate("/login");
    } catch (err) {
      console.error("Signup error:", err);
    }
  };

  const todayDate = new Date().toISOString().split("T")[0];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f0f2f5",
        px: 2,
      }}
    >
      <Paper
        elevation={4}
        sx={{ p: 4, width: "100%", maxWidth: 600, borderRadius: 3 }}
      >
        <Typography variant="h5" align="center" gutterBottom>
          Create Your Account
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* First + Last Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                error={!!formErrors.firstName}
                helperText={formErrors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                error={!!formErrors.lastName}
                helperText={formErrors.lastName}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={form.email}
                onChange={handleChange}
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
            </Grid>

            {/* Phone Number with Country Code */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                value={form.phoneNumber}
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, "");
                  if (value.length > 10) value = value.slice(0, 10);
                  setForm((prev) => ({ ...prev, phoneNumber: value }));
                  setFormErrors((prev) => ({ ...prev, phoneNumber: "" }));
                }}
                error={!!formErrors.phoneNumber}
                helperText={formErrors.phoneNumber}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TextField
                        select
                        value={form.countryCode}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            countryCode: e.target.value,
                          }))
                        }
                        SelectProps={{
                          native: true,
                        }}
                        variant="standard"
                        sx={{
                          width: 70,
                          "& select": { textAlign: "center" },
                        }}
                      >
                        <option value="91">+91</option>
                      </TextField>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Date of Birth */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Date of Birth"
                name="dateOfBirth"
                type="date"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: todayDate,
                  defaultValue: "2010-01-01", // Calendar opens at this date
                }}
                value={form.dateOfBirth}
                onChange={handleChange}
                error={!!formErrors.dateOfBirth}
                helperText={formErrors.dateOfBirth}
                onFocus={(e) => {
                  // If field is empty, set to default date when focused
                  if (!e.target.value) {
                    e.target.value = "2010-01-01";
                    e.target.showPicker && e.target.showPicker();
                  }
                }}
              />
            </Grid>

            {/* Password */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={handleChange}
                error={!!formErrors.password}
                helperText={formErrors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Confirm Password */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={form.confirmPassword}
                onChange={handleChange}
                error={!!formErrors.confirmPassword}
                helperText={formErrors.confirmPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Submit */}
            <Grid item xs={12}>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {loading ? "Signing up..." : "Sign Up"}
              </Button>
            </Grid>

            {/* Back to Login */}
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate("/login")}
              >
                Back to Login
              </Button>
            </Grid>

            {/* Error */}
            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}
          </Grid>
        </form>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 3, fontSize: "0.8rem", textAlign: "center" }}
        >
          © 2025 QuizzyIn. All rights reserved.
        </Typography>
      </Paper>
    </Box>
  );
}
